/**
 * useUserList Hook
 * React Query infinite query hook for paginated user list
 * Used in sidebar for user selection
 */

import { useInfiniteQuery } from '@tanstack/react-query';
import { fetchUsers } from '../api/calendarApi';

/**
 * User list infinite query hook for sidebar
 * Handles pagination and search
 *
 * @param {object} options - Query options
 * @param {string} options.search - Search query
 * @param {number} options.limit - Items per page
 * @param {boolean} options.enabled - Enable/disable query
 * @returns {object} React Query infinite result
 */
export const useUserList = ({ search = '', limit = 20, selectedUsersIds = [], enabled = true } = {}) => {
  const query = useInfiniteQuery({
    queryKey: ['calendar', 'users', search],
    queryFn: async ({ pageParam = 1 }) => {
      const result = await fetchUsers(pageParam, limit, selectedUsersIds, search);
      return result.data;
    },
    getNextPageParam: (lastPage, pages) => {
      if (!lastPage || !lastPage.firm_user_data) {
        return undefined;
      }

      const hasMore = lastPage.has_more;
      return hasMore ? pages.length + 1 : undefined;
    },
    enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: false,
  });

  // Flatten paginated data
  // getRdsUserList returns firm_users array
  const users = query.data?.pages?.flatMap((page) => page.firm_user_data || []) || [];
  const selectedUsers = query.data?.pages?.flatMap((page) => page.selected_firm_users || []) || [];

  return {
    ...query,
    users,
    selectedUsers,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    hasMore: query.hasNextPage,
    loadMore: query.fetchNextPage,
    isLoadingMore: query.isFetchingNextPage,
  };
};

export default useUserList;
